/*
 * <product-tabs> — WAI-ARIA APG Tabs widget for the product info column.
 *
 * Markup is rendered by snippets/product-tabs.liquid with the correct ARIA and
 * initial state already in place (so it is correct with no JS). This element
 * adds keyboard operability:
 *   - roving tabindex; Left/Right (wrap), Home, End move between tabs
 *   - AUTOMATIC activation: arrow keys switch the visible panel as focus moves
 *     (focus stays on the tab, never enters the panel); Enter / Space / click also select
 *   - inactive panels toggled via the `hidden` attribute
 *   - never steals focus on load
 *
 * Panels with no focusable children get tabindex="0" so keyboard users can
 * reach static content; panels that contain focusable children (links, the FAQ
 * <summary> elements) do not take a tab stop.
 */
if (!customElements.get('product-tabs')) {
  customElements.define(
    'product-tabs',
    class ProductTabs extends HTMLElement {
      connectedCallback() {
        this.tablist = this.querySelector('[role="tablist"]');
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
        if (!this.tablist || this.tabs.length === 0) return;

        this.onKeydown = this.onKeydown.bind(this);
        this.onClick = this.onClick.bind(this);
        this.tablist.addEventListener('keydown', this.onKeydown);
        this.tablist.addEventListener('click', this.onClick);

        // A panel becomes its own tab stop only when it has no focusable child.
        const focusable =
          'a[href], button:not([disabled]), summary, input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
        this.panels.forEach((panel) => {
          if (panel.querySelector(focusable)) {
            panel.removeAttribute('tabindex');
          } else {
            panel.setAttribute('tabindex', '0');
          }
        });

        // Normalise state to the server-rendered active tab. No focus moves here.
        const active =
          this.tabs.find((t) => t.getAttribute('aria-selected') === 'true') || this.tabs[0];
        this.select(active, { moveFocus: false });
      }

      disconnectedCallback() {
        this.tablist?.removeEventListener('keydown', this.onKeydown);
        this.tablist?.removeEventListener('click', this.onClick);
      }

      onClick(event) {
        const tab = event.target.closest('[role="tab"]');
        if (tab && this.tabs.includes(tab)) this.select(tab, { moveFocus: true });
      }

      onKeydown(event) {
        const tab = event.target.closest('[role="tab"]');
        if (!tab || !this.tabs.includes(tab)) return;
        const i = this.tabs.indexOf(tab);
        let target = null;

        switch (event.key) {
          case 'ArrowRight':
            target = this.tabs[(i + 1) % this.tabs.length];
            break;
          case 'ArrowLeft':
            target = this.tabs[(i - 1 + this.tabs.length) % this.tabs.length];
            break;
          case 'Home':
            target = this.tabs[0];
            break;
          case 'End':
            target = this.tabs[this.tabs.length - 1];
            break;
          case 'Enter':
          case ' ':
            event.preventDefault();
            this.select(tab, { moveFocus: true });
            return;
          default:
            return;
        }
        event.preventDefault();
        // Automatic activation: the panel swaps as focus moves between tabs.
        // Safe per APG here because select() only toggles `hidden` and keeps
        // focus on the tab — it never moves focus into the panel.
        this.select(target, { moveFocus: true });
      }

      // Activate a tab: update selection + show its panel.
      select(tab, { moveFocus }) {
        this.tabs.forEach((t) => {
          const selected = t === tab;
          t.setAttribute('aria-selected', selected ? 'true' : 'false');
          t.setAttribute('tabindex', selected ? '0' : '-1');
          t.classList.toggle('active', selected);
        });
        this.panels.forEach((panel) => {
          panel.hidden = panel.getAttribute('aria-labelledby') !== tab.id;
        });
        if (moveFocus) tab.focus();
      }
    }
  );
}
